import { getUserRecommendations } from '@atlaskit/smart-user-picker';
import {
  AsyncSelect,
  type FormatOptionLabelMeta,
  type GroupBase,
  type SelectProps
} from '@databases/atlaskit/Select';
import { getCloudId } from '@databases/database-state';
import { messages } from '@databases/intl/messages';
import { type CreateMentionNotificationProps } from '@databases/mention-notification/types';
import { type KnownUser, type UserReference } from '@databases/types/confluence';
import { type DatabaseField, DatabaseFieldType } from '@databases/types/database';
import { AvatarWithLabel, UserCard } from '@databases/user';
import {
  type MultiChange,
  MultiValueChangeType,
  type ValueChange
} from '@databases/utils/database/changesets';
import { generateId } from '@databases/utils/utils';
import React, { type ReactNode, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { getIdentity } from '@canvas/connection/identity';

import {
  CustomValueContainer,
  baseSelectStyleConfig,
  compactOptionSelectStyleConfig,
  isRenderedInSidePanelStyling
} from '../popover-edit-common/index.ts';

import { type ResolvedUser, type Value } from './types.ts';

const getOptionValue = (value: KnownUser | UserReference) => {
  return value.accountId;
};
type InputActionMeta = { action?: string };

const isFullUser = (value: KnownUser | UserReference): value is KnownUser => {
  return 'type' in value;
};

const isUserSelectOption = (
  value: ResolvedUser
): value is ResolvedUser & (KnownUser | UserReference) => 'accountId' in value;

interface UserSelectProps {
  resolvedUsers: ResolvedUser[];
  onChange: (value: Value[], change: ValueChange | null) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  contentId: string;
  field: DatabaseField<DatabaseFieldType.USER>;
  autoFocus?: boolean;
  isPending?: boolean;
  isRenderedInSidePanel?: boolean;
  isRenderedInContextualFieldMenu?: boolean;
  isMulti: boolean;
  addDataSectionRef: React.MutableRefObject<HTMLDivElement | null>;
  scheduleMentionNotification: (params: CreateMentionNotificationProps) => void;
  initialKey?: string;
}

const getUserRecommendationsParams = async (query: string) => {
  return {
    context: {
      contextType: 'contributors',
      principalId: (await getIdentity())?.accountId || '',
      productAttributes: {
        isEntitledConfluenceExternalCollaborator: false
      },
      productKey: 'confluence',
      siteId: getCloudId() || ''
    },
    includeGroups: false,
    includeTeams: false,
    includeUsers: true,
    maxNumberOfResults: 15,
    performSearchQueryOnly: false,
    query
  };
};

/**
 * An asynchronous select box that allows searching for users within the Confluence instance.
 */
const UserSelect: React.FunctionComponent<UserSelectProps> = ({
  resolvedUsers,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  isPending,
  contentId,
  field,
  isRenderedInSidePanel,
  isRenderedInContextualFieldMenu,
  isMulti,
  addDataSectionRef,
  scheduleMentionNotification,
  initialKey
}) => {
  const [inputValue, setInputValue] = useState(initialKey ?? '');
  const handleInputChange = useCallback((val: string, meta?: InputActionMeta) => {
    if (meta && meta.action !== 'input-change') {
      return;
    }
    setInputValue(val);
  }, []);
  const intl = useIntl();
  const formatMessage = intl.formatMessage;
  const selectValue = useMemo(
    () => resolvedUsers.filter(isUserSelectOption),
    [resolvedUsers]
  );

  const loadOptions = useCallback(
    async (query: string): Promise<(KnownUser | UserReference)[]> => {
      const data = await getUserRecommendations(
        await getUserRecommendationsParams(query),
        intl
      );

      return data.map((user) => ({
        accountId: user.id,
        displayName: user.name,
        type: 'known',
        profilePicture: {
          path: user.avatarUrl
        }
      }));
    },
    [intl]
  );

  const keepUnselectedUsers = useCallback(
    (
      options: (KnownUser | UserReference | GroupBase<KnownUser | UserReference>)[]
    ): (KnownUser | UserReference | GroupBase<KnownUser | UserReference>)[] =>
      options.filter((option) => {
        if (!('accountId' in option)) {
          return true;
        }

        const isAlreadySelected = resolvedUsers.some(
          (u) => 'accountId' in u && u.accountId === option.accountId
        );

        return !isAlreadySelected;
      }),
    [resolvedUsers]
  );

  const onItemAdd = useCallback(
    async (val: Value) => {
      const userValues = isMulti ? [...resolvedUsers, val] : [val];

      const delta: MultiChange<DatabaseFieldType.USER> = isMulti
        ? {
            type: MultiValueChangeType.APPEND,
            value: val
          }
        : {
            type: MultiValueChangeType.SET_TO,
            value: [val]
          };

      onChange(userValues, {
        type: DatabaseFieldType.USER,
        delta
      });

      const user = val as UserReference;
      if (
        field.settings.mentionNotificationEnabled &&
        contentId &&
        user.displayName &&
        !isRenderedInSidePanel
      ) {
        scheduleMentionNotification({
          mentions: [
            {
              mentionLocalId: val._id,
              mentionedUserAccountId: user.accountId,
              displayName: user.displayName
            }
          ],
          fieldId: field.id
        });
      }
    },
    [
      resolvedUsers,
      isMulti,
      onChange,
      field.settings.mentionNotificationEnabled,
      field.id,
      contentId,
      scheduleMentionNotification,
      isRenderedInSidePanel
    ]
  );

  const handleChange: SelectProps<KnownUser | UserReference, boolean>['onChange'] =
    useCallback(
      (value) => {
        if (Array.isArray(value)) {
          setInputValue('');
          const userValues = value.map((user) => ({
            _id:
              typeof (user as { _id?: unknown })._id === 'string'
                ? (user as unknown as { _id: string })._id
                : generateId(),
            accountId: user.accountId,
            displayName: user.displayName
          }));

          onChange(userValues, {
            type: DatabaseFieldType.USER,
            delta: {
              type: MultiValueChangeType.SET_TO,
              value: userValues
            }
          });
          return;
        }
        if (!value) {
          setInputValue('');
          onChange([], {
            type: DatabaseFieldType.USER,
            delta: {
              type: MultiValueChangeType.SET_TO,
              value: []
            }
          });
          return;
        }
        const valToAdd = {
          _id: generateId(),
          accountId: 'accountId' in value ? value.accountId : '',
          displayName: 'displayName' in value ? value.displayName : ''
        };
        setInputValue('');
        onItemAdd(valToAdd);
      },
      [onChange, onItemAdd]
    );

  const formatOptionLabel = useCallback(
    (
      value: KnownUser | UserReference,
      meta: FormatOptionLabelMeta<KnownUser | UserReference>
    ): ReactNode => {
      if (meta.context === 'menu') {
        if (isFullUser(value)) {
          return (
            <AvatarWithLabel
              userOrGroup={value}
              label={value.displayName}
              size={isRenderedInContextualFieldMenu ? 'xsmall' : undefined}
            />
          );
        } else {
          return (
            <UserCard
              accountId={value.accountId}
              displayName={value.displayName}
              variant="avatar"
              size={isRenderedInContextualFieldMenu ? 'xsmall' : undefined}
            />
          );
        }
      }
      return (
        <UserCard
          accountId={value.accountId}
          displayName={value.displayName}
          variant="avatar"
          size="xsmall"
        />
      );
    },
    [isRenderedInContextualFieldMenu]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      if (onBlur) {
        onBlur(e);
      }
      if (!isRenderedInSidePanel && !isRenderedInContextualFieldMenu) {
        addDataSectionRef.current?.getElementsByTagName('input')[0].focus();
      }
    },
    [onBlur, addDataSectionRef, isRenderedInContextualFieldMenu, isRenderedInSidePanel]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isMulti) {
      e.preventDefault();
      addDataSectionRef.current?.getElementsByTagName('input')[0].blur();
    }
  };

  return (
    // FIXME: Accessibility issue - jsx-a11y/no-static-element-interactions
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div style={{ width: '100%' }} onKeyDown={handleKeyDown}>
      <AsyncSelect<KnownUser | UserReference, boolean>
        classNamePrefix="user-picker"
        onChange={handleChange}
        formatOptionLabel={formatOptionLabel}
        loadOptions={loadOptions}
        getOptionValue={getOptionValue}
        modifyOptions={keepUnselectedUsers}
        onFocus={onFocus}
        onBlur={handleBlur}
        blurInputOnSelect={false}
        closeMenuOnSelect={false}
        autoFocus={autoFocus}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        placeholder={formatMessage(messages['user-picker-popover.search-text'])}
        loadingMessage={() =>
          formatMessage(messages['field-settings.select-picker-loading-text'])
        }
        isLoading={isPending}
        styles={
          isRenderedInContextualFieldMenu
            ? compactOptionSelectStyleConfig
            : isRenderedInSidePanel
              ? isRenderedInSidePanelStyling
              : baseSelectStyleConfig
        }
        spacing={isRenderedInContextualFieldMenu ? 'compact' : undefined}
        controlShouldRenderValue={Boolean(isRenderedInContextualFieldMenu)}
        value={isRenderedInContextualFieldMenu ? selectValue : null}
        {...(isRenderedInContextualFieldMenu && {
          isClearable: resolvedUsers.length > 0,
          isMulti
        })}
        {...(!isRenderedInSidePanel &&
          !isRenderedInContextualFieldMenu && {
            menuPortalTarget: null,
            defaultMenuIsOpen: true,
            menuIsOpen: true,
            components: {
              IndicatorsContainer: () => null,
              ValueContainer: CustomValueContainer
            }
          })}
      />
    </div>
  );
};

export default UserSelect;
